import * as maptilersdk from '@maptiler/sdk';
import { GeocodingControl } from '@maptiler/geocoding-control/maptilersdk';
import Spiderfy from '@nazka/map-gl-js-spiderfy';
import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '@utils/i18n/i18n';
import { faSlidersH } from '@fortawesome/pro-solid-svg-icons';
import Button from '@App/components/Button/Button';
import LanguageSwitch from './controls/LanguageSwitch/LanguageSwitch';

import {
  addMarkerImagesToMap, createPopup, getInitialCenterAndZoom, moveLayers, 
} from './map.utils';
import Sidepanel from './Sidepanel/Sidepanel';
import sources from './style/style.sources';
import layers from './style/style.layers';

import '@maptiler/sdk/dist/maptiler-sdk.css';
import '@maptiler/geocoding-control/style.css';
import './map.styl';

maptilersdk.config.apiKey = import.meta.env.VITE_MAPTILER_TOKEN;

function Map() {
  const location = useLocation();
  const { t, lang } = useTranslation();
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const spiderfy = useRef(null);
  const spiderfyProviderIcon = useRef(null);
  const spiderfyParkingCounter = useRef(null);
  const breakpoint = useSelector(state => state.windowObserver.breakpoint);
  const [vehicles, setVehicles] = useState([]);
  const [stations, setStations] = useState([]);
  const [sidepanelIsOpen, setSidepanelIsOpen] = useState(1);
  const popupRef = useRef(null);

  const queryParams = new URLSearchParams(location.search);
  const hideLanguageControl = useRef(!!queryParams?.get('hideLangCtrl'));

  const isMobile = breakpoint?.maxWidth <= 768;

  useEffect(() => {
    const handleSourceData = () => moveLayers(mapRef);

    if (!mapRef.current) {
      const { center, zoom } = getInitialCenterAndZoom();

      mapRef.current = new maptilersdk.Map({
        container: mapContainer.current,
        style: '69cd4e91-ccb3-46b9-8c2c-b5299b42875c',
        center,
        zoom,
        maxZoom: 18.9,
        fadeDuration: 0,
        navigationControl: false,
        geolocateControl: false,
        attributionControlOptions: false,
        forceNoAttributionControl: true,
      });

      mapRef.current.dragRotate.disable();
      mapRef.current.keyboard.disable();
    
      mapRef.current.addControl(new maptilersdk.AttributionControl({
        compact: false,
        customAttribution: '<a href="https://nazka.be" rel="noopener" target="_blank">Nazka Mapps</a>',
      }), 'bottom-left');

      mapRef.current.addControl(new maptilersdk.MaptilerNavigationControl({
        showCompass: false,
      }), 'bottom-left');

      mapRef.current.addControl(new maptilersdk.GeolocateControl({
      }), 'bottom-left');

      mapRef.current.addControl(new maptilersdk.FullscreenControl({
        container: document.querySelector('body'),
      }), 'bottom-left');

      if (!hideLanguageControl.current) {
        mapRef.current.addControl(new LanguageSwitch(), 'bottom-left');
      }

      mapRef.current.on('load', async () => {
        Object.keys(sources).forEach((sourceId) => {
          mapRef.current.addSource(sourceId, sources[sourceId]);
        });
        await addMarkerImagesToMap(mapRef.current);
        layers.forEach(layer => mapRef.current.addLayer(layer));

        mapRef.current.on('click', 'vehicles-clusters', async (e) => {
          if (mapRef.current.getZoom() >= 19) return; 
          const features = mapRef.current.queryRenderedFeatures(e.point, {
            layers: ['vehicles-clusters'],
          });
    
          const clusterId = features[0].properties.cluster_id;
          const clusterCoords = features[0].geometry.coordinates;
          const expansionZoom = await mapRef.current.getSource('vehicles').getClusterExpansionZoom(clusterId);

          mapRef.current.flyTo({
            center: clusterCoords,
            zoom: expansionZoom,
            speed: 1,
          });
        });

        mapRef.current.on('mousemove', (e) => {
          const features = mapRef.current.queryRenderedFeatures(e.point);
      
          if (features.length > 0) {
            if (
              features.map(f => f.layer.id).find((id) => {
                const clusterLeaves = Array(100).fill(0).map((_, i) => `vehicles-spiderfy-helper1-spiderfy-leaf${i}`);
                return ['vehicles-clusters', 'vehicles', ...clusterLeaves].includes(id);
              })) {
              mapRef.current.getCanvas().style.cursor = 'pointer';
            } else {
              mapRef.current.getCanvas().style.cursor = '';
            }
          } else {
            mapRef.current.getCanvas().style.cursor = '';
          }
        });

        mapRef.current.on('click', (e) => {
          const feature = mapRef.current.queryRenderedFeatures(e.point, {
            layers: ['station-counter', 'vehicles-providers', 'vehicles'],
          })[0];

          if (!feature) return;

          if (popupRef.current) {
            popupRef.current.root?.unmount();
            popupRef.current.popup?.remove();
          }

          popupRef.current = createPopup(
            feature,
            e,
            maptilersdk,
            mapRef.current,
          );
          popupRef.current.popup.addTo(mapRef.current);
        });

        spiderfy.current = new Spiderfy(mapRef.current, {
          minZoomLevel: 18.9,
          zoomIncrement: 0,
          closeOnLeafClick: false,
          spiderLeavesLayout: {
            ...layers.find(l => l.id === 'vehicles').layout,
            'icon-anchor': 'center',
          },
          spiderLeavesPaint: {
            ...layers.find(l => l.id === 'vehicles').paint,
            'icon-opacity': 1,
          },
          renderMethod: '3D',
          spiralOptions: { leavesOffset: [0, 15], leavesSeparation: 40, legLengthFactor: 3.75 },
          onLeafClick: (f, e) => {
            if (popupRef.current) {
              popupRef.current.root?.unmount();
              popupRef.current.popup?.remove();
            }

            popupRef.current = createPopup(f, e, maptilersdk, mapRef.current);
            popupRef.current.popup.addTo(mapRef.current);
          },
        });
        spiderfyProviderIcon.current = new Spiderfy(mapRef.current, {
          minZoomLevel: 18.9,
          zoomIncrement: 0,
          spiderLegsAreHidden: true,
          closeOnLeafClick: false,
          spiderLeavesLayout: {
            ...layers.find(l => l.id === 'vehicles-providers').layout,
            'icon-anchor': 'center',
          },
          spiderLeavesPaint: {
            ...layers.find(l => l.id === 'vehicles-providers').paint,
            'icon-opacity': 1,
          },
          renderMethod: '3D',
          spiralOptions: { leavesOffset: [0, 15], leavesSeparation: 40, legLengthFactor: 3.75 },
        });
        spiderfyParkingCounter.current = new Spiderfy(mapRef.current, {
          minZoomLevel: 18.9,
          zoomIncrement: 0,
          spiderLegsAreHidden: true,
          closeOnLeafClick: false,
          spiderLeavesLayout: {
            ...layers.find(l => l.id === 'station-counter').layout,
            'icon-anchor': 'center',
            'icon-offset': [2, 1],
            'text-offset': [1.1, -0.85],
          },
          spiderLeavesPaint: {
            ...layers.find(l => l.id === 'station-counter').paint,
            'icon-opacity': ['case', ['has', 'marker_counter'], 1, 0],
          },
          renderMethod: '3D',
          spiralOptions: { leavesOffset: [0, 15], leavesSeparation: 40, legLengthFactor: 3.75 },
        });
        spiderfy.current.applyTo('vehicles-clusters');
        spiderfyProviderIcon.current.applyTo('vehicles-spiderfy-helper1');
        spiderfyParkingCounter.current.applyTo('vehicles-spiderfy-helper2');
      });

      mapRef.current.on('sourcedata', handleSourceData);
    }

    return () => {
      mapRef.current.off('sourcedata', handleSourceData);
    };
  }, [t]);

  useEffect(() => {
    if (!mapRef.current) return;

    mapRef.current.setLanguage(
      Object.values(maptilersdk.Language).find(l => l.code === lang),
    );

    mapRef.current._controls.forEach((control) => {
      if (control instanceof GeocodingControl) {
        mapRef.current.removeControl(control);
      }
    });
  
    const searchControl = new GeocodingControl({
      noResultsMessage: t('map-control_search_no-results'),
      placeholder: t('map-control_search_placeholder'),
      country: 'be',
      language: lang,
      types: ['municipality', 'municipal_district', 'address'],
    });

    searchControl.on('pick', (e) => {
      if (window?.gtag) {
        window.gtag('event', 'location_selected', {
          event_category: 'Location',
          event_label: e?.feature?.place_name,
          location_place_name: e?.feature?.place_name,
          location_type: e?.feature?.place_type?.[0],
          location_id: e?.feature?.id,
        });
      }
    });

    const zoomInButton = document.querySelector('.maplibregl-ctrl-zoom-in');
    const zoomOutButton = document.querySelector('.maplibregl-ctrl-zoom-out');
    const fullScreenControl = document.querySelector('.maplibregl-ctrl-fullscreen');
    const geolocateControl = document.querySelector('.maplibregl-ctrl-geolocate');

    if (zoomInButton) zoomInButton.title = t('map-control_zoom-in_tooltip');
    if (zoomOutButton) zoomOutButton.title = t('map-control_zoom-out_tooltip');
    if (fullScreenControl) fullScreenControl.title = t('map-control_fullscreen_tooltip');
    if (geolocateControl) {
      setInterval(() => {
        geolocateControl.title = t('map-control_my-location_tooltip');
        geolocateControl['aria-label'] = t('map-control_my-location_tooltip');
      }, 250);
    }
  
    mapRef.current.addControl(searchControl, 'top-left');
  }, [t, lang]);

  useEffect(() => {
    if (!mapRef.current) return;
    if (isMobile) {
      if (sidepanelIsOpen === 1) setSidepanelIsOpen(false);
    } else {
      mapRef.current.easeTo({
        padding: {
          left: 0,
          top: 0,
          right: sidepanelIsOpen && !isMobile ? 350 : 0,
          bottom: 0,
        },
        duration: 1000,
      });
    }
    setTimeout(() => {
      mapRef.current.setPadding({
        left: 0,
        top: 0,
        right: sidepanelIsOpen && !isMobile ? 350 : 0,
        bottom: 0, 
      });
    }, 1000);
  }, [sidepanelIsOpen, isMobile]);

  useEffect(() => {
    if (!mapRef.current || (!vehicles && !stations)) return;
    if (spiderfy.current) {
      spiderfy.current.unspiderfyAll();
      spiderfy.current.applyTo('vehicles-clusters');
    }
    if (spiderfyProviderIcon.current) {
      spiderfyProviderIcon.current.unspiderfyAll();
      spiderfyProviderIcon.current.applyTo('vehicles-spiderfy-helper1');
    }
    if (spiderfyParkingCounter.current) {
      spiderfyParkingCounter.current.unspiderfyAll();
      spiderfyParkingCounter.current.applyTo('vehicles-spiderfy-helper2');
    }
    const addVehicles = () => {
      mapRef.current.getSource('vehicles').setData({
        type: 'FeatureCollection',
        features: [...vehicles, ...stations],
      });
    }
    if (mapRef.current.getSource('vehicles')) {
      addVehicles();
    } else {
      mapRef.current.once('load', () => addVehicles());
    }
  }, [vehicles, stations]);

  return (
    <div className={`map${sidepanelIsOpen ? ' map--with-padding' : ''}`} ref={mapContainer}>
      <Sidepanel 
        sidepanelIsOpen={sidepanelIsOpen}
        setSidepanelIsOpen={setSidepanelIsOpen}
        setVehicles={setVehicles}
        setStations={setStations}
      />
      <Button 
        className="map__sidepanel-toggle"
        onClick={() => setSidepanelIsOpen(!sidepanelIsOpen)}
        icon={faSlidersH}
        rootElProps={{ title: t('filter_title') }}
      >
        {t('filter_title')}
      </Button>
    </div>
  );
}

export default Map;
